import {
  signalStore,
  withState,
  withMethods,
  patchState,
  getState,
} from '@ngrx/signals';
import {
  GeoJSON,
  Map,
  Layer,
  Control,
  PathOptions,
  geoJSON,
  LatLng,
  LeafletMouseEvent,
  Direction,
} from 'leaflet';
import {
  getUnit,
  Indicator,
  isWegweiserIndicator,
} from '../../models/indicator';
import { Colorset, fallbackColors } from '../../models/colorset';
import { DataEntry, IndicatorData } from '../../models/indicator-data';
import {
  Feature,
  FeatureCollection,
  PoiMarkerOptions,
} from '../../models/geo-data';
import { LeafletMapboxService } from '../../services/leaflet-mapbox.service';
import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  inject,
  Injector,
} from '@angular/core';
import { isNumber } from 'chart.js/helpers';
import { ScaleService } from '../../services/scale.service';
import { Scale } from '../../models/scale';
import { DataLayerTypeIcon } from '../data-layer-type/data-layer-type.selectors';
import {
  removeAllEntities,
  removeEntity,
  setEntity,
  withEntities,
} from '@ngrx/signals/entities';
import {
  ScaleMode,
  WegweiserIndicator,
  WidgetConfig,
} from '../../services/grid-widget-config.service';
import { ColorsetStore } from '../colorset/colorset.state';
import { IndicatorStore } from '../indicator/indicator.state';
import { LegendComponent } from '../../components/molecules/legend/legend.component';
import { ExtendedChartData } from '../../models/chartjs-extensions';
import { IndicatorDataStore } from '../indicatpr-data/indicator-data.state';
import { GlobalsStore } from '../globals.state';
import { SafeHtml } from '@angular/platform-browser';
import { MapLegendScope } from '../../models/commune';
import { Chart, ChartConfiguration } from 'chart.js';
import { TooltipHelperService } from '../../services/widget/tooltip-helper.service';
import { generateHarmoniousColors } from '../../utility/color.utility';
import { GridStore } from '../grid/grid.state';
import { debounceIntent } from '../../utility/app.utility';

export interface IdentifiableLayer extends Layer {
  id: string;
  scales?: Scale[];
  indicator?: Indicator;
}

interface FeatureInfo {
  feature: Feature;
  value: number | null;
  indicator: Indicator | null;
}

export interface leafletLegend {
  legendControl: Control;
  legendComponentRef: ComponentRef<LegendComponent>;
}

export interface MapState {
  _id: string;
  map: Map | null;
  geoJsonLayer: GeoJSON | null;
  tileLayer: Layer | null;
  tileLayerType: string | undefined;
  scales: Scale[] | null;
  legendControl: Control | null;
  indicator: Indicator | null;
  indicatorData: IndicatorData | null;
  data: DataEntry | null;
  comparisonValues: any | null;
  colors: string[];
  colorset: Colorset | null;
  config: WidgetConfig | null;
  featureHighlight: any | null;
  mouseOverFeatureKey: string | null;
  indicatorLegends: Record<
    string,
    {
      legendControl: Control;
      legendComponentRef: ComponentRef<LegendComponent>;
      scales: Scale[];
      icons: DataLayerTypeIcon[];
    }
  >;
}

export const initialState: MapState = {
  _id: '',
  map: null,
  geoJsonLayer: null,
  tileLayer: null,
  tileLayerType: undefined,
  indicator: null,
  indicatorData: null,
  legendControl: null,
  colorset: null,
  featureHighlight: null,
  mouseOverFeatureKey: null,
  data: null,
  comparisonValues: null,
  scales: null,
  colors: fallbackColors,
  config: null,
  indicatorLegends: {},
};

const defaultStyle: PathOptions = {
  color: '#505050',
  weight: 1,
  opacity: 1,
  fillOpacity: 0,
  //dashArray: [4, 2],
};

export const MapStore = signalStore(
  withEntities<IdentifiableLayer>(), // Manage layers as entities by ID
  withState(initialState),
  withMethods(
    (
      store,
      mapboxService = inject(LeafletMapboxService),
      tooltipHelperService = inject(TooltipHelperService),
      scaleService = inject(ScaleService),
      globalsStore = inject(GlobalsStore),
      colorsetStore = inject(ColorsetStore),
      indicatorStore = inject(IndicatorStore),
      gridStore = inject(GridStore),
      dataStore = inject(IndicatorDataStore),
      appRef = inject(ApplicationRef),
      resolver = inject(ComponentFactoryResolver),
      injector = inject(Injector)
    ) => ({
      initializeMap(mapContainerId: string, mapContainer: HTMLElement): Map {
        const map = mapboxService.initializeMap(mapContainer);
        patchState(store, { map, _id: mapContainerId });
        return map;
      },
      setGeoJsonLayer(layer: GeoJSON | null) {
        patchState(store, { geoJsonLayer: layer });
      },
      setTileLayer(layer: Layer | null) {
        patchState(store, { tileLayer: layer });
      },
      setLegendControl(control: Control | null) {
        patchState(store, { legendControl: control });
      },
      highlightFeature(e: LeafletMouseEvent) {
        const feature: any = e.target.feature;
        const featureKey = feature.properties.key || feature.id;
        if (featureKey === store.featureHighlight()?.feature.properties.key)
          return;

        const indicator: any = store.indicator();
        const data = store.data();
        const newFeatureInfo: FeatureInfo = {
          feature: feature,
          indicator: indicator || undefined,
          value: data ? data[featureKey] : null,
        };

        patchState(store, { featureHighlight: newFeatureInfo });
        globalsStore.triggerAtlasEvent('highlightDistrict', {
          widgetId: store._id(),
          key: String(featureKey),
          type: 'feature',
        });
      },
      resetHighlight(e: any) {
        if (!store.featureHighlight()?.feature.properties.key) return;
        patchState(store, { featureHighlight: null });
        globalsStore.triggerAtlasEvent('highlightDistrict', {
          widgetId: store._id(),
          key: undefined,
        });
      },
      setMapConfig(config: WidgetConfig): void {
        const mapState = getState(store);

        let indicator = mapState.indicator;
        let indicatorData = mapState.indicatorData;

        if (
          !indicator ||
          indicator._id !== config.indicator ||
          !config.dataLayer ||
          config.dataLayer !== indicatorData?.dataLayer
        ) {
          indicator = indicatorStore.getById(config.indicator);
          indicatorData = dataStore.getByDataLayerAndIndicator(
            config.dataLayer,
            config.indicator
          );
        }

        const parent = indicator?.parent
          ? indicatorStore.getById(indicator.parent)
          : undefined;

        const colorset: Colorset | null = colorsetStore.getFirstByIds([
          indicatorData?.colorSet || '',
          indicator?.defaultColorSet || '',
          parent?.defaultColorSet || '',
          config.colorset || '',
          config.map?.colorset || '',
        ]);

        const invert = !!(
          (indicatorData && indicatorData.invert) ||
          (indicator && indicator.defaultInvert) ||
          config.map?.legend.invert
        );

        let colors: string[] = [...(colorset?.colors || fallbackColors)];
        if (invert) colors = colors.reverse();

        const tileLayer =
          !mapState.tileLayer ||
          mapState.tileLayerType !== config.map?.tileLayer
            ? this.changeTileLayer(config.map?.tileLayer || 'light')
            : mapState.tileLayer;

        patchState(store, {
          config,
          colorset,
          tileLayer,
          tileLayerType: config.map?.tileLayer,
          indicator,
          indicatorData,
          colors,
        });
      },
      clearLayers(): void {
        patchState(store, {
          geoJsonLayer: null,
          tileLayer: null,
        });
      },
      createStyledMarker(feature: any, options: PoiMarkerOptions) {
        let color = '#FFFFFF';
        if (!options || !options.icon) return;
        if (options.scales && options.data) {
          const key = feature.properties?.key;
          const value = isNumber(options.data[key]) ? options.data[key] : null;

          color =
            value !== null
              ? scaleService.getColor(value, options.scales, store.colors())
              : '#efefef';
        }
        return mapboxService.createMarker(
          options.latlng,
          options.icon?.svg,
          color,
          options.icon?.color || '#000000',
          options.size
        );
      },
      styleFn(
        feature: any,
        data: DataEntry | undefined,
        scales: Scale[] | undefined
      ): PathOptions {
        if (data && scales && feature) {
          const mapConfig = store.config()?.map;
          const key = feature.properties?.key || feature.id;
          const value = data[key] ?? null;
          const color =
            value !== null
              ? scaleService.getColor(value, scales, store.colors())
              : '#efefef';

          if (gridStore.options()?.highlightedFeature?.key === String(key)) {
            return {
              ...defaultStyle,
              color: '#222222',
              fillColor: color,
              dashArray: [2, 6],
              weight: 3,
              ...{ fillOpacity: mapConfig?.districtOpacity || 0.7 },
            };
          }

          if (
            gridStore
              .options()
              ?.selectedFeatures?.some(item => item.key === String(key))
          ) {
            return {
              ...defaultStyle,
              color: '#444444',
              fillColor: color,
              dashArray: undefined,
              weight: 3,
              ...{ fillOpacity: mapConfig?.districtOpacity || 0.7 },
            };
          }

          return {
            ...defaultStyle,
            color: color,
            dashArray: undefined,
            weight: 2,
            ...{ fillOpacity: mapConfig?.districtOpacity || 0.7 },
          };
        } else {
          return defaultStyle;
        }
      },
      changeTileLayer(type: string): any {
        const tileLayer = store.tileLayer();
        const map = store.map();
        if (!map) return;
        if (tileLayer) {
          mapboxService.clearLayers(map, tileLayer);
          map.removeLayer(tileLayer);
        }

        if (type === 'blank') {
          return;
        }

        const newTileLayer = mapboxService.createMapboxGLLayer(type);
        newTileLayer.addTo(map);
        map.invalidateSize();
        return newTileLayer;
      },
      addIndicatorLegendComponent(
        // for poi
        map: Map,
        scales: Scale[],
        indicator: Indicator,
        icons: DataLayerTypeIcon[],
        comparisonData?: { [key: string]: number }
      ): {
        legendControl: Control;
        legendComponentRef: ComponentRef<LegendComponent>;
      } {
        this.removeLegendForIndicator(indicator._id);

        return this.addLegendComponent(
          map,
          scales,
          store.colors(),
          indicator,
          icons,
          comparisonData
        );
      },
      addLegendComponent(
        map: Map,
        scales: Scale[],
        colors: string[],
        indicator: Indicator | WegweiserIndicator,
        icons: DataLayerTypeIcon[],
        comparisonData?: { [key: string]: number },
        wegweiserIndicator?: WegweiserIndicator
      ) {
        const factory = resolver.resolveComponentFactory(LegendComponent);
        const legendComponentRef = factory.create(injector);

        legendComponentRef.instance.scales = scales;
        legendComponentRef.instance.colors = colors;
        legendComponentRef.instance.indicator = indicator;
        legendComponentRef.instance.icons = icons;
        legendComponentRef.instance.size =
          store.config()?.map?.legend.size || 'large';
        legendComponentRef.instance.displayedComparisonValues =
          store.config()?.comparisonValues;

        if (comparisonData) {
          legendComponentRef.instance.comparisonData = comparisonData;
        }

        appRef.attachView(legendComponentRef.hostView);
        const domElem = (legendComponentRef.hostView as any)
          .rootNodes[0] as HTMLElement;

        const legendControl = new Control({
          position: store.config()?.map?.legend.position || 'bottomright',
        });

        legendControl.onAdd = () => {
          return domElem;
        };

        legendControl.addTo(map);
        return { legendControl, legendComponentRef };
      },
      removeLegendForIndicator(indicatorId: string) {
        const legends = store.indicatorLegends();
        const legend = legends[indicatorId];
        const map = store.map();
        if (!legend || !map) return; // Legend doesn't exist

        this.removeLegend(map, legend);
        // Remove the legend control and component
        if (legend.legendControl && map) {
          map.removeControl(legend.legendControl);
        }
        if (legend.legendComponentRef) {
          appRef.detachView(legend.legendComponentRef.hostView);
          legend.legendComponentRef.destroy();
        }

        delete legends[indicatorId];
        patchState(store, { indicatorLegends: legends });
      },
      removeLegend(map: Map, legend: leafletLegend) {
        appRef.detachView(legend.legendComponentRef.hostView);
        legend.legendComponentRef.destroy();
        map.removeControl(legend.legendControl);
      },
      addPointLayer(
        id: string,
        typeName: string,
        data: DataEntry | undefined,
        geoJsonData: GeoJSON.FeatureCollection,
        icon: DataLayerTypeIcon,
        indicator: Indicator | undefined
      ) {
        const map = store.map();
        if (map && geoJsonData) {
          const config = store.config();
          if (!config) return;

          const existingLayer = store.entityMap()[id];
          if (existingLayer) {
            map.removeLayer(existingLayer);
          }

          const indicatorData = dataStore.getByDataLayerAndIndicator(
            config.dataLayer,
            config.indicator
          );

          const existingLegend = indicator
            ? store.indicatorLegends()?.[indicator._id]
            : undefined;

          const existingScales = existingLegend?.scales;
          const existingIcons = existingLegend?.icons;
          const scales =
            indicatorData &&
            indicatorData.legend &&
            indicatorData.legend.length &&
            indicatorData.colorSet
              ? indicatorData.legend
              : indicator &&
                  indicator.legend?.length &&
                  indicator.defaultColorSet
                ? indicator.legend
                : data
                  ? scaleService.generateScales(
                      indicatorData && config.map?.legend?.legendScope === 'all'
                        ? indicatorData.data
                        : data,
                      store.colors(),
                      config.map?.legend.scaleMode,
                      config.map?.legend.numberOfScales,
                      existingScales
                    )
                  : undefined;

          const layer: IdentifiableLayer = Object.assign(
            this.createPointLayer(typeName, data, geoJsonData, scales, icon),
            { id, scales, indicator }
          );

          layer.addTo(map);

          // Add legend for the new layer
          if (scales && indicator) {
            const { legendControl, legendComponentRef } =
              this.addIndicatorLegendComponent(
                map,
                scales,
                indicator,
                existingIcons ? [...existingIcons, ...[icon]] : [icon]
              );

            const legends = store.indicatorLegends();

            patchState(store, {
              indicatorLegends: {
                ...legends,
                [indicator._id]: {
                  legendControl,
                  legendComponentRef,
                  scales,
                  icons: [icon],
                },
              },
            });
          }

          patchState(store, setEntity(layer)); // Use `id` as the unique key
        }
      },
      createPointLayer(
        typeName: string,
        data: DataEntry | undefined,
        geoJsonData: GeoJSON.FeatureCollection,
        scales: Scale[] | undefined,
        icon: DataLayerTypeIcon
      ) {
        return geoJSON(geoJsonData, {
          style: feature => this.styleFn(feature, data, scales),
          pointToLayer: (feature: Feature, latlng: LatLng) => {
            return this.createStyledMarker(feature, {
              latlng,
              data,
              scales,
              colors: store.colors(),
              icon,
              size: store.config()?.map?.poiSize || 1,
            }) as Layer;
          },
          onEachFeature: (feature, layer) => {
            if (feature) {
              const popupContent = this.buildPoiPopup(
                typeName,
                feature,
                icon.svg
              );
              layer.bindPopup(popupContent);
            }
          },
        });
      },
      getPointLayer(id: string) {
        return store.entityMap()[id];
      },
      /**
       * Returns Poi-Popup-Markup
       * @param typeName - The type of the POI.
       * @param poi - The POI object containing its properties.
       * @param icon - Optional SafeHtml for an icon.
       * @returns {string} - The generated popup HTML as a string.
       */
      buildPoiPopup(typeName: string, poi: any, icon?: SafeHtml): string {
        // Generate the top section of the popup with the icon and name
        const generateHeader =
          (): string => `<h3 class="flex gap-3 align-items-center flex-wrap-reverse">
          ${poi.properties.name || ''}
          <span class="text-xs p-chip py-2 ml-auto">${typeName}</span>
        </h3>`;

        // Generate an entry if a specific property exists
        const generatePropertyEntry = (
          label: string,
          value?: string
        ): string =>
          value
            ? `<h4 class="mt-3 mb-1">${label}:</h4><div>${value}</div>`
            : '';

        // Generate the body section with optional properties
        const bodyContent = `
          ${poi.properties.description ? `<i>${poi.properties.description}</i>` : ''}
          ${generatePropertyEntry('Adresse', poi.properties.address)}
          ${generatePropertyEntry('Kontakt', poi.properties.contact)}
          ${generatePropertyEntry('Altersklasse', poi.properties.ageClass)}
          ${generatePropertyEntry('Plätze', poi.properties.places)}
          ${generatePropertyEntry('Öffnungszeiten', poi.properties.openingTimes)}
          ${generatePropertyEntry('Besonderheit', poi.properties.specials)}
          ${
            poi.properties.information
              ? generatePropertyEntry(
                  'Informationen',
                  poi.properties.information.replace(
                    /href/g,
                    'target="blank" href'
                  )
                )
              : ''
          }
        `;

        // Wrap the content in a container and return
        return `<div class="poi-popup">
            ${generateHeader()}
            ${bodyContent}
          </div>`;
      },
      removePointLayer(layerId: string) {
        const map = store.map();
        if (!map) return;

        const layer = store.entityMap()[layerId];
        if (layer) {
          map.removeLayer(layer);
        }

        patchState(store, removeEntity(layerId));
      },
      removeAllPointLayer() {
        const map = store.map();
        if (!map) return;

        // Iterate over all layers in the entityMap
        const layers = store.entityMap();
        Object.values(layers).forEach(layer => {
          if (layer) {
            map.removeLayer(layer); // Remove each layer from the map
          }
        });

        // Remove all legends
        Object.keys(store.indicatorLegends()).forEach(indicatorId => {
          this.removeLegendForIndicator(indicatorId);
        });

        patchState(store, removeAllEntities());
      },
      getBounds() {
        const bounds = store.config()?.map?.view.bounds;
        if (!bounds) return undefined;
        return mapboxService.getBoundsFromObject(bounds);
      },
      getScales(
        indicatorData: IndicatorData,
        indicator: Indicator,
        data?: DataEntry,
        legendOptions?: {
          legendScope?: MapLegendScope;
          scaleMode?: ScaleMode;
          numberOfScales?: number;
        }
      ) {
        const indicatorScales = indicatorStore.getScales(indicator);
        return indicatorData &&
          indicatorData.colorSet &&
          indicatorData.legend &&
          indicatorData.legend.length
          ? indicatorData.legend
          : indicatorScales
            ? indicatorScales
            : data && Object.keys(data).length
              ? scaleService.generateScales(
                  indicatorData && legendOptions?.legendScope === 'all'
                    ? indicatorData.data
                    : data,
                  store.colors(),
                  legendOptions?.scaleMode,
                  legendOptions?.numberOfScales
                )
              : undefined;
      },
      checkHighlightChange() {
        const oldHighlightKey = store.mouseOverFeatureKey() || null;
        const newHighlightKey = gridStore.options()?.highlightedFeature || null;

        return oldHighlightKey === newHighlightKey;
      },
      createGeoJsonLayer(
        data: DataEntry | undefined,
        geoJsonData: FeatureCollection,
        config: WidgetConfig,
        chartData?: ExtendedChartData,
        icon?: any
      ) {
        const currentConfig = store.config();
        const map = store.map();

        if (map && geoJsonData) {
          const geoJsonLayer = store.geoJsonLayer();

          const indicatorData = dataStore.getByDataLayerAndIndicator(
            config.dataLayer,
            config.indicator
          );

          const indicator =
            indicatorStore.getById(config.indicator) ||
            config.wegweiserData?.indicators.at(0) ||
            null;

          const scales = this.getScales(indicatorData, indicator, data, {
            legendScope: currentConfig?.map?.legend.legendScope,
            scaleMode: currentConfig?.map?.legend.scaleMode,
            numberOfScales: currentConfig?.map?.legend.numberOfScales,
          });

          if (geoJsonLayer && this.checkHighlightChange()) {
            map.removeLayer(geoJsonLayer);
          } else if (geoJsonLayer) {
            geoJsonLayer.eachLayer(layer => {
              if (
                typeof (layer as any).setStyle === 'function' &&
                'feature' in layer
              ) {
                (layer as any).setStyle(
                  this.styleFn((layer as any).feature, data, scales)
                );
              }
            });

            patchState(store, {
              geoJsonLayer: geoJsonLayer,
              scales: scales,
              indicator: indicator,
              data: data,
              mouseOverFeatureKey:
                gridStore.options()?.highlightedFeature?.key || null,
              comparisonValues: config.groupedData?.comparisonValues || null,
            });

            return;
          }

          const tooltipStyle = currentConfig?.map?.captions.style;
          const newGeoLayer = icon
            ? this.createPointLayer(
                'ttt',
                data,
                geoJsonData,
                scales,
                icon
              ).addTo(map)
            : geoJSON(geoJsonData, {
                style: feature => this.styleFn(feature, data, scales),
                onEachFeature: (feature, layer) => {
                  if (feature.properties) {
                    const key = feature.properties?.key || feature.id;

                    const popupContent = this.generateLeafletTooltip(
                      feature,
                      data && data[key] && isNumber(data[key])
                        ? data[key]
                        : null,
                      config.map?.captions,
                      indicator ? indicator._id || '' : '',
                      indicator ? getUnit(indicator) : '',
                      indicator ? isWegweiserIndicator(indicator) : false
                    );

                    const content =
                      popupContent || document.createElement('div');

                    const featureKey = String(key);
                    const isDisabled =
                      config.disabledCharts?.includes(featureKey);
                    const shouldHaveChart =
                      config.map?.captions.chart &&
                      chartData &&
                      chartData.datasets &&
                      chartData.datasets.length > 1 &&
                      !isDisabled;

                    let chartContent: HTMLCanvasElement | null = null;

                    // Only generate chart if it should exist and (doesn't exist yet or data changed)
                    if (shouldHaveChart) {
                      chartContent = this.generateOnMapChart(
                        store._id(),
                        feature,
                        config,
                        chartData,
                        store.colors()
                      );
                    }

                    if (chartContent || popupContent) {
                      if (chartContent) content.append(chartContent);
                      layer.bindTooltip(content, {
                        permanent: true,
                        direction: (tooltipStyle === 'blank'
                          ? 'center'
                          : tooltipStyle || 'top') as Direction,
                      });
                      setTimeout(() => {
                        layer.getTooltip()?.update();
                        // workaround placement after chart is rendered
                      }, 150);
                    } else if (!shouldHaveChart) {
                      layer.unbindTooltip();
                    }

                    const DEBOUNCE_MS = 150;

                    const intent = debounceIntent(
                      this.highlightFeature,
                      DEBOUNCE_MS
                    );

                    layer.on({
                      mouseover: intent.mouseover,
                      mouseout: e => {
                        intent.mouseout(e); // Always cancel possible waiting highlight!
                        this.resetHighlight(e); // ...and ALWAYS run your logic to un-highlight!
                      },
                      click: this.selectDistrictAndZoomToFeature,
                    });
                  }
                },
              }).addTo(map);

          patchState(store, {
            geoJsonLayer: newGeoLayer,
            scales: scales,
            indicator: indicator,
            data: data,
            mouseOverFeatureKey:
              gridStore.options()?.highlightedFeature?.key || null,
            comparisonValues: config.groupedData?.comparisonValues || null,
          });

          if (!newGeoLayer) return;
        }
      },
      selectDistrictAndZoomToFeature(e: any) {
        const key = e.sourceTarget.feature.properties.key;
        globalsStore.triggerAtlasEvent('markDistrict', { key });
      },
      generateLeafletTooltip(
        feature: any,
        value: number | null,
        captions: any,
        indicatorId: string,
        unit: string,
        isWegweiserIndicator = false
      ) {
        if (!captions) return null;
        // todo: categorical; decimals; unit;

        const germanNumber = globalsStore.getFormattedValue(
          value,
          indicatorId,
          false,
          unit,
          'short',
          isWegweiserIndicator
        );

        const keyHTML = captions.key
          ? '<div><strong>' +
            (feature.properties.key || feature.properties.gkz) +
            '</strong></div>'
          : '';
        const nameHTML = captions.name
          ? '<div>' + feature.properties.name + '</div>'
          : '';
        const valueHTML =
          value && captions.value ? '<div>' + germanNumber + '</div>' : '';

        const html = keyHTML + nameHTML + valueHTML;
        const content = document.createElement('div');
        content.classList.add('tooltip-content');
        content.innerHTML = html;

        return html.trim() ? content : null;
      },

      /**
       * Generates a bar chart on a map feature using provided data and colors.
       *
       * @param {string} id - The unique identifier for the chart.
       * @param {any} feature - The map feature object containing relevant properties for the chart.
       * @param {WidgetConfig} config - The map widget-configuration relevant for tooltips for the chart.
       * @param {ExtendedChartData} [chartData] - Optional chart data to define labels and datasets for the chart.
       * @param {string[]} [colors] - Optional array of colors to use for the chart's datasets.
       * @return {HTMLCanvasElement|null} The generated canvas element containing the chart or null if insufficient data is provided.
       */
      generateOnMapChart(
        id: string,
        feature: any,
        config: WidgetConfig,
        chartData?: ExtendedChartData,
        colors?: string[]
      ): HTMLCanvasElement | null {
        const canvas = document.createElement('canvas');
        canvas.style.height = '80px';
        canvas.style.width = '100px';
        canvas.id = `chart-${id}--${feature.properties.key}`;

        let dataIdx = 0;
        if (!chartData || !chartData.labels || !chartData.datasets) return null;

        const labels = chartData.labels.filter((label: any, idx) => {
          if (label === feature.properties.name) {
            dataIdx = idx;
            return true;
          }
          return false;
        });

        const datasets = chartData.datasets.map((dataset: any) => ({
          ...dataset,
          data: [...dataset.data],
        }));

        datasets.shift();
        const result: any = datasets.map((data: any, idx) => {
          const values: any[] = data.data;
          const newValues: any[] = [values.at(dataIdx)]; // Keep only the relevant value
          const harmonicColorset = generateHarmoniousColors(
            colors || fallbackColors
          );
          const color = harmonicColorset?.at(idx);
          return {
            data: newValues,
            label: data.label,
            backgroundColor: color,
            borderColor: color,
          };
        });

        const chart: ChartConfiguration = {
          type: 'bar',
          data: {
            labels,
            datasets: result,
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            plugins: {
              legend: {
                display: false,
              },
              datalabels: { display: false },
              tooltip: {
                enabled: false, // Disable the default tooltip
                external: (context: any) => {
                  // Call the service method to render the HTML tooltip
                  tooltipHelperService.createOrUpdateTooltip(
                    context.tooltip,
                    'chart-js--tooltip-container',
                    'bar',
                    config,
                    true
                  );
                },
              },
            },
            scales: {
              x: { display: false },
              y: { display: true },
            },
          },
        };

        // Wait for DOM insertion before rendering the chart
        setTimeout(() => {
          new Chart(canvas, chart);
        }, 250);

        return canvas;
      },
    })
  )
);
